import { helpers } from '@vuelidate/validators';

import type { SupportedFileExtensions } from '@/types';

export const hasAtSymbol = helpers.regex(/@/); // Checks for the presence of "@"

export const hasNoSpecialCharacters = helpers.regex(/^[^±~`!#$%^&*()=[\]{}|;:',<>?\\/]+$/); // Disallows special characters

export const hasNoUmlauts = helpers.regex(/^[^\u00c4-\u00ff]+$/i); // Disallows umlauts (ä, ö, ü, etc.)

export const isValidDomain = helpers.regex(/@(([A-z\-0-9]+\.)+[A-z]{2,})$/); // Ensures valid domain structure

export const hasValidLocalPart = (email: string | undefined): boolean => {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return true; // Skip check if no valid email
  }

  const localPart = email.split('@')[0];

  // Validate local part: No consecutive dots, no leading/trailing dots, no spaces
  const regex = /^(?!.*\.\.)[^.\s][^\s]*[^.\s]$/;
  return regex.test(localPart);
};

export function isDateFormat(value: string | undefined): boolean {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  return !dateMatch ? false : true;
}

export function isYoutubeUrl(value: string | undefined): boolean {
  if (!value) {
    return true;
  }

  const match: RegExpMatchArray | null = value.match(
    /^http(?:s?):\/\/(?:www\.)?youtu(?:be\.com\/watch\?v=|\.be\/)[\w-]+$/,
  );
  return !match ? false : true;
}

// eslint-disable-next-line complexity
export function isValidDate(value: string | undefined): boolean {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dateMatch) {
    return false;
  }

  const [, dateDay, dateMonth, dateYear] = dateMatch.map((item) => parseInt(item, 10));

  // The following logic from https://stackoverflow.com/a/6178341

  // Check the ranges of month and year
  if (dateYear < 1000 || dateYear > 3000 || dateMonth == 0 || dateMonth > 12) {
    return false;
  }

  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Adjust for leap years
  if (dateYear % 400 == 0 || (dateYear % 100 != 0 && dateYear % 4 == 0)) {
    monthLength[1] = 29;
  }

  // Check the range of the day
  return dateDay <= 0 || dateDay > monthLength[dateMonth - 1] ? false : true;
}

export function isFutureDate(
  value: string | undefined,
  { includeToday }: { includeToday: boolean } = { includeToday: true },
): boolean {
  // cover AvailableFrom.BY_AGREEMENT usecase
  if (value === undefined) {
    return true;
  }

  const dateMatch: RegExpMatchArray | null = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dateMatch) {
    return false;
  }

  const [, dateDay, dateMonth, dateYear] = dateMatch.map((item) => parseInt(item, 10));

  let futureDate;
  try {
    futureDate = new Date(dateYear, dateMonth - 1, dateDay);
  } catch {
    return false;
  }

  // Initialising a new Date with Date.now() allows easy mocks for testing.
  const currentTime = new Date(Date.now());

  const currentDate = new Date(
    currentTime.getFullYear(),
    currentTime.getMonth(),
    currentTime.getDate(),
  );

  if (includeToday) {
    return futureDate >= currentDate;
  }
  return futureDate > currentDate;
}

function stripHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/** Encodes plain text for safe insertion into HTML (inverse of stripHtml). */
export function escapeHtml(value: string): string {
  const element = document.createElement('div');
  element.textContent = value;
  return element.innerHTML;
}

export function isHtmlTextLengthBetween(
  value: string,
  min?: number,
  max?: number,
  allowEmpty = false,
): boolean {
  const text = stripHtml(value);

  if (!text && allowEmpty) {
    return true;
  }

  const minValid = min === undefined || text.length >= min;
  const maxValid = max === undefined || text.length <= max;

  return minValid && maxValid;
}

export function htmlTextLengthBetween(
  min: number,
  max: number,
  options: Record<string, boolean> = {
    allowEmpty: false,
  },
): any {
  return helpers.withParams(
    { type: 'htmlTextLengthBetween', min: min, max: max },
    (value: string | undefined) => {
      if (!value && value !== '') {
        return false;
      }

      return isHtmlTextLengthBetween(value, min, max, options.allowEmpty);
    },
  );
}

/**
 * We check the URL if it was specified with the correct protocol.
 *
 * @param value - value
 */
export function urlStartsWithHttps(value: string | undefined): boolean {
  return !value ? true : value.startsWith('https://');
}

export function isSafeHttpUrl(value: string | undefined): value is string {
  if (!value) {
    return false;
  }

  try {
    const parsedUrl = new URL(value);
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
  } catch {
    return false;
  }
}

export const inputStartsWithLetter = helpers.regex(/^((?!_)[A-z])+/);

export function isValidFiletype(
  fileName: string,
  supportedTypes: Set<SupportedFileExtensions>,
): boolean {
  if (!fileName || !fileName.includes('.')) {
    return false;
  }
  const extension = fileName.split('.').pop() as string;
  return supportedTypes.has(extension.toLowerCase() as SupportedFileExtensions);
}
